import { sessionHeaders } from "./dajia-session-key";
export type Subject = {
	id: string;
	name: string;
	legal_name: string;
	aliases: string[];
	website: string;
};
export type Fact = {
	id: string;
	subject_id: string;
	text: string;
	source: string;
	verified_on: string;
	enabled: boolean;
	evidence?: {
		document_id: string;
		title?: string;
		page?: number;
		period?: string;
		excerpt: string;
		method: string;
		sha256: string;
		[key: string]: unknown;
	};
};
export type Question = {
	id: string;
	subject_id: string;
	category: string;
	text: string;
	blind: boolean;
	enabled: boolean;
};
export type Model = {
	id: string;
	name: string;
	endpoint: string;
	model: string;
	key_env: string;
	enabled: boolean;
	parameters?: Record<string, unknown> | null;
};
export type Config = {
	preset_version: number;
	revision: number;
	monitoring_enabled: boolean;
	opinion_enabled: boolean;
	monitoring_days: number;
	opinion_hours: number;
	monitoring_hour: number;
	opinion_hour: number;
	subjects: Subject[];
	facts: Fact[];
	questions: Question[];
	models: Model[];
};
export type Run = {
	scheduled_at?: string | null;
	id: string;
	kind: string;
	status: string;
	started_at: string;
	finished_at: string | null;
	next_at: string;
	success: number;
	failed: number;
	errors: string[];
	revision: number;
};
export type Sample = {
	observations?: {
		legal_name_present: boolean;
		official_url_present: boolean;
		time_marked: boolean;
	};
	id: string;
	run_id: string;
	subject_id: string;
	model_id: string;
	model_name: string;
	model: string;
	question: Question;
	answer: string;
	mentioned: boolean;
	collected_at: string;
};
export type News = {
	event_category?: string;
	event_level?: "high" | "low" | "info";
	latest_published_at?: string | null;
	latest_summary?: string;
	latest_provider?: string;
	id: string;
	subject_id: string;
	title: string;
	summary: string;
	url: string;
	source: string;
	published_at: string | null;
	collected_at: string;
	risk_keywords: string[];
};
export type Dashboard = {
	subjects: Subject[];
	facts: Fact[];
	question_count: number;
	model_count: number;
	monitoring_days: number;
	opinion_hours: number;
	monitoring_enabled: boolean;
	opinion_enabled: boolean;
	runs: Run[];
	samples: Sample[];
	models?: Array<{ id: string; name: string; model: string; enabled: boolean }>;
	evaluations?: Evaluation[];
	news: News[];
	generated_at: string;
};
export type Evaluation = {
	id: string;
	sample_id: string;
	run_id: string;
	model_id: string;
	evaluated_at: string;
	version: string;
	result: {
		mentioned: boolean;
		blind: boolean;
		brand_count: number;
		target_rank: number | null;
		metrics: Record<
			string,
			{
				value: number | null;
				denominator?: number;
				eligible?: boolean;
				scenario?: string;
				hit?: boolean;
			}
		>;
	};
};
export type ConfigResponse = {
	config: Config;
	availability: Record<string, boolean>;
	search_configured: boolean;
	search_provider: string;
	search_key_env: string;
};
export const stamp = (s?: string | null) =>
	s ? new Date(s).toLocaleString("zh-CN", { hour12: false }) : "尚未运行";
export async function api<T>(
	path: string,
	token = "",
	body?: unknown,
): Promise<T> {
	const r = await fetch(`/api/dajia/${path}`, {
		method: body === undefined ? "GET" : "POST",
		credentials: "same-origin",
		cache: "no-store",
		headers: {
			"Content-Type": "application/json",
			...sessionHeaders(),
			...(token ? { Authorization: `Bearer ${token}` } : {}),
		},
		...(body === undefined ? {} : { body: JSON.stringify(body) }),
	});
	if (!r.ok) {
		if (r.status === 401 && path !== "login" && path !== "session") {
			window.dispatchEvent(new Event("dajia-session-expired"));
		}
		throw new Error((await r.text()) || `请求失败 (${r.status})`);
	}
	return r.json() as Promise<T>;
}
